import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { navGroups, useCategoryTree } from '../../features/categories/hooks/useCategories'
import { useLocaleSegment } from '../../hooks/useLocale'
import { CITIES, DEFAULT_CITY } from '../../lib/cities'
import { getIcon } from '../../lib/icons'
/*
 * ✅ ASYMMETRY #8 IS GONE, AND THIS IS WORTH NOTICING. Docs/34 § 1b.2 warned
 * that **sixteen import symbols across four files** have to be removed by hand
 * when hiding, or `pnpm lint` fails on unused imports — and re-added by hand
 * when restoring. That was only true while hiding meant DELETING the markup.
 *
 * Now the markup lives in `DataColumns` below and is simply not rendered, so
 * every import here stays legitimately used in both states. **No import
 * bookkeeping in either direction.**
 */
import { showAppSurfaces } from '../../lib/appSurfaces'
import {
  companyNav,
  directoryPath,
  exploreNav,
  helpNav,
  type NavItem,
  routes,
  socialLinks,
} from '../../lib/navigation'
import { Container } from '../ui/Container'
import { Skeleton } from '../ui/Skeleton'
import { AppStoreBadges } from './AppStoreBadges'
import { LanguageSwitcher } from './LanguageSwitcher'
import { Logo } from './Logo'

const linkClasses =
  'touch-target inline-flex items-center justify-start rounded-md type-body-sm text-muted-foreground transition-colors duration-hover ease-out hover:text-foreground'

type FooterColumnProps = {
  title: string
  children: ReactNode
}

const FooterColumn = ({ title, children }: FooterColumnProps) => (
  <div>
    <h3 className="type-overline text-foreground">{title}</h3>
    <ul className="mt-2 flex flex-col">{children}</ul>
  </div>
)

const NavColumn = ({ title, items }: { title: string; items: NavItem[] }) => {
  const { t } = useTranslation()

  return (
    <FooterColumn title={title}>
      {items.map((item) => (
        <li key={`${title}-${item.to}`}>
          <Link to={item.to} className={linkClasses}>
            {t(item.labelKey)}
          </Link>
        </li>
      ))}
    </FooterColumn>
  )
}

const FOOTER_CATEGORY_LIMIT = 6

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE TWO COLUMNS MADE OF DATA — hidden with everything else in `'hidden'`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Six category links plus "All categories", and seven city links: **fifteen
 * links into the directory from every page on the site**, which makes the footer
 * the single largest funnel it has. That is why these were the first thing
 * hidden on 7 Oct and the first thing back that evening.
 *
 * 🔴 THEY ARE THEIR OWN COMPONENT SO THE QUERY GOES WITH THEM, AND THAT IS NOT
 * COSMETIC — IT IS ASYMMETRY #11 FROM Docs/34 § 1b.2 MADE STRUCTURAL.
 * `useCategoryTree` is a HOOK, so it cannot be called conditionally; left in
 * `Footer`, hiding the markup would leave the request firing on **every route**
 * for a footer nobody can see. Moving the columns into a child that is not
 * rendered at all takes the hook with them.
 *
 * ⚠️ THIS HOOK AND `CategoryGrid` ARE A PAIR ON ONE QUERY KEY —
 * `qk.categories.tree(locale)` — so when both are visible, `/` makes one request
 * for the two of them. They are gated by the same switch for that reason. **If
 * either is ever shown without the other, the survivor becomes the sole caller
 * and pays for the request alone.**
 */
const DataColumns = () => {
  const { t } = useTranslation()
  const localeSegment = useLocaleSegment()

  /* The same cached tree the homepage uses, so the footer can never disagree
     with the grid above it. On a phone the footer is below the fold, which is
     why the failure mode here is "render nothing but the 'all categories' link"
     rather than an error panel: nobody scrolled this far to be told a fetch
     failed. */
  const tree = useCategoryTree()
  const categories = navGroups(tree.data ?? []).slice(0, FOOTER_CATEGORY_LIMIT)

  return (
    <>
      <FooterColumn title={t('footer.columns.categories')}>
        {tree.isPending
          ? Array.from({ length: FOOTER_CATEGORY_LIMIT }, (_, index) => (
              <li key={index} className="py-2">
                <Skeleton className="h-4 w-28" />
              </li>
            ))
          : categories.map((category) => (
              <li key={category.id}>
                <Link
                  to={directoryPath(localeSegment, DEFAULT_CITY.slug, category.slug)}
                  className={linkClasses}
                >
                  {category.name}
                </Link>
              </li>
            ))}
        <li>
          <Link to={routes.categories} className={`${linkClasses} font-semibold`}>
            {t('footer.allCategories')}
          </Link>
        </li>
      </FooterColumn>

      <FooterColumn title={t('footer.columns.cities')}>
        {/* Each city link takes the first nav CATEGORY from the database,
            so it lands on a directory page that exists. No slug is
            written here — until the taxonomy has loaded the links go to
            the category index, which is a page rather than a guess. */}
        {CITIES.slice(0, 7).map((city) => {
          const leadCategory = categories[0]

          return (
            <li key={city.slug}>
              <Link
                to={
                  leadCategory
                    ? directoryPath(localeSegment, city.slug, leadCategory.slug)
                    : routes.categories
                }
                className={linkClasses}
              >
                {t('footer.cityLink', { city: city.name })}
              </Link>
            </li>
          )
        })}
      </FooterColumn>
    </>
  )
}

export const Footer = () => {
  const { t } = useTranslation()
  const year = new Date().getFullYear()
  const withData = showAppSurfaces()

  return (
    <footer className="mt-20 border-t border-border bg-card">
      <Container as="div" className="py-12 lg:py-16">
        <div className="grid gap-10 lg:grid-cols-12">
          {/* Brand + app */}
          <div className="lg:col-span-3">
            <Logo />
            <p className="mt-4 max-w-sm type-body text-muted-foreground">{t('footer.tagline')}</p>
            <AppStoreBadges className="mt-6" />
          </div>

          {/*
            ═══════════════════════════════════════════════════════════════════
            🔴 THE TRACK CHANGES WITH THE COLUMNS. THIS IS ASYMMETRY #3.
            ═══════════════════════════════════════════════════════════════════

            FIVE COLUMNS when the data columns are shown — two data (categories,
            cities) and three grouped page lists from `lib/navigation.ts`. The
            client asked for a site that reads as a structured platform, and the
            footer is where a visitor goes to see how big something is.

            THREE COLUMNS when they are not — and `xl:grid-cols-5` MUST GO WITH
            THEM. Three lists in a five-column track leave two empty columns of
            air to the right of "Help", which reads as a **failed render** rather
            than a short footer. Docs/34 § 1b.2 lists this as a step somebody
            would miss; it is a ternary now so they cannot.

            Five is never offered below `xl` in either state: five inside nine of
            twelve at 1024 px leaves ~140 px a column and wraps "Report a
            problem" onto two lines. The 1 → 2 → 3 ramp below that is unchanged.
          */}
          <div
            className={`grid gap-8 sm:grid-cols-2 md:grid-cols-3 lg:col-span-9 ${
              withData ? 'xl:grid-cols-5' : ''
            }`}
          >
            {withData ? <DataColumns /> : null}

            <NavColumn title={t('footer.columns.explore')} items={exploreNav} />
            <NavColumn title={t('footer.columns.company')} items={companyNav} />
            <NavColumn title={t('footer.columns.help')} items={helpNav} />
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-6 border-t border-border pt-8 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-1">
            <p className="type-body-sm text-muted-foreground">
              {t('footer.copyright', { year })}
            </p>
            <p className="type-caption text-muted-foreground">{t('footer.orgNumber')}</p>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {/*
              ⚠️ GUARDED ON LENGTH, AND THAT GUARD IS LOAD-BEARING AS OF 13 OCT.
              `socialLinks` is EMPTY — the three it held pointed at strangers'
              profiles on every page of the live site, because they were written
              under the old product name and nobody owns an `avexa` handle.
              `lib/navigation.ts` has the whole account.

              Without this check an empty array renders an empty `<ul>`, which
              leaves the flex row's gap and reads as icons that failed to load.
              With it, the footer simply has no social row — and one real
              account brings the whole block back untouched.
            */}
            {socialLinks.length > 0 ? (
              <ul className="flex items-center gap-1">
                {socialLinks.map((social) => {
                  const Icon = getIcon(social.icon)

                  return (
                    <li key={social.id}>
                      <a
                        href={social.href}
                        target="_blank"
                        rel="noreferrer noopener"
                        aria-label={t('footer.socialLink', { network: social.name })}
                        className="touch-target inline-flex items-center justify-center rounded-full text-muted-foreground transition-colors duration-hover ease-out hover:bg-muted hover:text-foreground"
                      >
                        <Icon aria-hidden="true" className="size-5" />
                      </a>
                    </li>
                  )
                })}
              </ul>
            ) : null}

            <LanguageSwitcher />
          </div>
        </div>
      </Container>
    </footer>
  )
}
